/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { AppStateService } from '@shared/services/app-state.service';
import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Adjust, AdjustEvent } from '@awesome-cordova-plugins/adjust';
import { FacebookEvents } from 'capacitor-facebook-events';
import { TikTokEvents } from 'capacitor-tiktok-events';
import { getApps, initializeApp } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { environment } from 'src/environments/environment';
import { FirebaseAnalyticsService } from '@shared/services/firebase-analytics.service';
import { PosthogService } from '@shared/services/posthog.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  public userData: any;
  private lastStoreFromTypeEvent: string = '';
  private analytics: any;
  private adjustInitialized: boolean = false;
  private tiktokInitialized: boolean = false;
  private tiktokInitPromise: Promise<void> | null = null;

  constructor(
    private appStateService: AppStateService,
    private firebaseAnalyticsService: FirebaseAnalyticsService,
    private posthog: PosthogService
  ) {
    if (!Capacitor.isNativePlatform()) {
      this.initializeFirebaseOnlyWeb();
    } else {
      // Collection starts disabled via Info.plist / AndroidManifest.
      // Re-enable only for production so test builds (same Firebase project)
      // do not send automatic in_app_purchase or custom events.
      FirebaseAnalytics.setCollectionEnabled({ enabled: environment.production })
        .then(() => {
          console.log(`Firebase Analytics collection enabled: ${environment.production}`);
        })
        .catch(err => console.error('FirebaseAnalytics.setCollectionEnabled failed:', err));
      void this.initializeTikTok();
    }
  }

  private initializeAdjust() {
    if (!this.adjustInitialized && Capacitor.isNativePlatform() && this.appStateService.adjustConfig) {
      try {
        Adjust.initSdk(this.appStateService.adjustConfig);
        this.adjustInitialized = true;
        console.log('Adjust SDK initialized successfully');
      } catch (error) {
        console.error('Error initializing Adjust SDK:', error);
      }
    }
  }

  /** Resolves per-OS TikTok credentials (ios/android). Supports legacy flat env shape. */
  private getTikTokPlatformConfig(): { appId: string; tiktokAppId: string; accessToken: string } | null {
    const cfg = (environment as any).tiktok;
    if (!cfg) {
      return null;
    }
    const isIos = Capacitor.getPlatform() === 'ios';
    const platformCfg = isIos ? cfg.ios : cfg.android;
    if (platformCfg?.appId && platformCfg?.tiktokAppId && platformCfg?.accessToken) {
      return {
        appId: platformCfg.appId,
        tiktokAppId: platformCfg.tiktokAppId,
        accessToken: platformCfg.accessToken
      };
    }
    // Legacy flat shape (single shared tiktokAppId/accessToken)
    const appId = isIos ? cfg.iosAppId : cfg.androidAppId;
    if (appId && cfg.tiktokAppId && cfg.accessToken) {
      return { appId, tiktokAppId: cfg.tiktokAppId, accessToken: cfg.accessToken };
    }
    return null;
  }

  private isTikTokConfigured(): boolean {
    const platformCfg = this.getTikTokPlatformConfig();
    if (!platformCfg) {
      return false;
    }
    const values = [platformCfg.appId, platformCfg.tiktokAppId, platformCfg.accessToken];
    return values.every((v) => typeof v === 'string' && v.length > 0 && !String(v).startsWith('TODO_'));
  }

  private async initializeTikTok(): Promise<void> {
    if (this.tiktokInitialized || !Capacitor.isNativePlatform()) {
      return;
    }
    if (!environment.production) {
      console.log('TikTok initialize skipped (production: false)');
      return;
    }
    if (!this.isTikTokConfigured()) {
      console.log('TikTok initialize skipped (placeholder credentials in environment.tiktok)');
      return;
    }
    if (this.tiktokInitPromise) {
      return this.tiktokInitPromise;
    }

    const platformCfg = this.getTikTokPlatformConfig();
    if (!platformCfg) {
      return;
    }

    this.tiktokInitPromise = TikTokEvents.initialize({
      appId: platformCfg.appId,
      tiktokAppId: platformCfg.tiktokAppId,
      accessToken: platformCfg.accessToken,
      debug: false,
      disablePaymentTracking: true
    }).then(() => {
      this.tiktokInitialized = true;
      console.log('TikTok SDK initialized successfully');
    }).catch((error) => {
      console.error('Error initializing TikTok SDK:', error);
      this.tiktokInitPromise = null;
    });

    return this.tiktokInitPromise;
  }

  private sendTikTokPurchaseEvent(productId: string, value: number, currency: string): void {
    if (!this.tiktokInitialized) {
      void this.initializeTikTok().then(() => {
        if (this.tiktokInitialized) {
          this.sendTikTokPurchaseEvent(productId, value, currency);
        }
      });
      return;
    }

    TikTokEvents.trackPurchase({
      value,
      currency,
      contentId: productId,
      contentType: 'product',
      quantity: 1
    }).then(() => {
      console.log(`TikTok Purchase sent - Product: ${productId}, Value: ${value}, Currency: ${currency}`);
    }).catch((error) => {
      console.error('Error sending TikTok Purchase:', error);
    });
  }


  public initializeFirebaseOnlyWeb() {
    /**
 * Platform: Web
 * Configure and initialize the firebase app.
 * @param options - firebase web app configuration options
 * */
    if (!getApps().length) {
      const firebaseConfig = {
        apiKey: "AIzaSyAtgYtgAuKGflP79cWo0__j--53ihQTQmY",
        authDomain: "lefty-4f864.firebaseapp.com",
        projectId: "lefty-4f864",
        storageBucket: "lefty-4f864.firebasestorage.app",
        messagingSenderId: "888967854315",
        appId: "1:888967854315:web:f42bc98e191c143186e6ae",
        measurementId: "G-RZ8KS9SLBK"
      };

      // Initialize Firebase
      const app = initializeApp(firebaseConfig);
      this.analytics = getAnalytics(app);
      this.firebaseAnalyticsService.setAnalyticsInstance(this.analytics);
    }
  }

  // ============================================================================
  // Eventos Fyra — atualizados em 2026-05-27 pra alinhar com a conta Adjust do Fyra.
  // Antes os tokens eram cópia dos do Veggly (8b3...e os outros tokens veggly) e o
  // Adjust descartava 100% dos eventos custom porque os tokens não pertenciam ao
  // app do Fyra. Renomeação avocado→premium e carrots→coins reflete a nomenclatura
  // usada no Adjust dashboard do Fyra.
  //
  // Constants TS mantidos com os nomes antigos (avcdXxx, crrtXxx) pra não quebrar
  // os 74 call sites — só os campos internos (eventName, adjustEvent) foram trocados.
  //
  // Eventos sem token (adjustEvent: '') = não cadastrados no Adjust do Fyra.
  // Continuam disparando pra Firebase e Facebook normalmente.
  // ============================================================================

  public static signUpEvent: any = {
    eventName: 'sign_up',
    adjustEvent: 'gozu7k'
  };

  public static likeEvent: any = {
    eventName: 'like',
    adjustEvent: 'kymr1z'
  }

  public static matchEvent: any = {
    eventName: 'match',
    adjustEvent: 'ounfht'
  }

  public static superLikeEvent: any = {
    eventName: 'super_like',
    adjustEvent: 'ne8lal'
  }

  public static sendMessageEvent: any = {
    eventName: 'send_message',
    adjustEvent: 'ub58fw'
  }

  public static market: any = {
    eventName: 'market',
    adjustEvent: ''
  }

  public static mktFromMenu: any = {
    eventName: 'mkt_from_menu',
    adjustEvent: ''
  }

  // Premium Store Events (renomeados de "avocado" no Veggly)

  public static avocado: any = {
    eventName: 'premium',
    adjustEvent: 'b17p4j'
  }

  public static avcdFromMkt: any = {
    eventName: 'premium_from_menu',
    adjustEvent: 'yg2wb6'
  }

  public static avcdFromLikedme: any = {
    eventName: 'premium_from_likedme',
    adjustEvent: 'x97h9o'
  }

  public static avcdFromTeleport: any = {
    eventName: 'premium_from_teleport',
    adjustEvent: '5fc1qh'
  }

  // No Fyra Adjust tokens for indicator sources yet (Firebase/Meta names only).
  public static avcdFromIndicator: any = {
    eventName: 'avcd_from_indicator',
    adjustEvent: ''
  }

  public static avcdFromIndicatorExpl: any = {
    eventName: 'avcd_from_indicator_expl',
    adjustEvent: ''
  }

  public static avcdFromCoins: any = {
    eventName: 'premium_from_coins',
    adjustEvent: 'rg3h0q'
  }

  public static avcdFromPromotion: any = {
    eventName: 'premium_from_promotion',
    adjustEvent: 'b0ehzj'
  }

  public static avcdFromFirstOpen: any = {
    eventName: 'premium_from_first_open',
    adjustEvent: 'tl1qid'
  }

  public static avcdFromFirstPhoto: any = {
    eventName: 'premium_from_first_photo',
    adjustEvent: 'omcepe'
  }

  public static avcdFromReadConf: any = {
    eventName: 'premium_from_read_conf',
    adjustEvent: 'wzz8mo'
  }

  public static avcdCheckoutSuccess: any = {
    eventName: 'premium_checkout_success',
    adjustEvent: 'e7ne3m',
    sendAdjustPurchase: true
  }

  public static avcdCheckoutSuccessFromMkt: any = {
    eventName: 'premium_checkout_success_from_menu',
    adjustEvent: '9qdv6h'
  }

  public static avcdCheckoutSuccessFromLikedMe: any = {
    eventName: 'premium_checkout_success_from_likedme',
    adjustEvent: '4xo469'
  }

  public static avcdCheckoutSuccessFromTeleport: any = {
    eventName: 'premium_checkout_success_from_teleport',
    adjustEvent: 'w5d792'
  }

  // No Fyra Adjust tokens for indicator checkout sources yet (Firebase/Meta names only).
  public static avcdCheckoutSuccessFromIndicator: any = {
    eventName: 'avcd_checkout_success_from_indicator',
    adjustEvent: ''
  }

  public static avcdCheckoutSuccessFromIndicatorExpl: any = {
    eventName: 'avcd_checkout_success_from_indicator_expl',
    adjustEvent: ''
  }

  public static avcdCheckoutSuccessFromFirstOpen: any = {
    eventName: 'premium_checkout_success_from_first_open',
    adjustEvent: 'x9rz6t'
  }

  public static avcdCheckoutSuccessFromFirstPhoto: any = {
    eventName: 'premium_checkout_success_from_first_photo',
    adjustEvent: '58fsd3'
  }

  public static avcdCheckoutSuccessFromReadConf: any = {
    eventName: 'premium_checkout_success_from_read_conf',
    adjustEvent: 'msyyp7'
  }

  public static avcdCheckoutSuccessFromCoins: any = {
    eventName: 'premium_checkout_success_from_coins',
    adjustEvent: 'c4hhz5'
  }

  // Coins Store Events (renomeados de "carrots" no Veggly)

  public static carrots: any = {
    eventName: 'coins',
    adjustEvent: 'fyrosy'
  }

  // Fyra Adjust uses coins_from_premium (not Veggly crrt_from_mkt).
  public static crrtFromMkt: any = {
    eventName: 'coins_from_premium',
    adjustEvent: '7pjf8l'
  }

  public static crrtFromSuperlike: any = {
    eventName: 'coins_from_superlike',
    adjustEvent: 'v0nwk0'
  }

  // No Fyra Adjust token for boost source yet (Firebase/Meta name only).
  public static crrtFromBoost: any = {
    eventName: 'crrt_from_boost',
    adjustEvent: ''
  }

  // TODO: preencher adjustEvent com o token real do painel do Adjust para "crrt_from_intro".
  public static crrtFromIntro: any = {
    eventName: 'crrt_from_intro',
    adjustEvent: ''
  }

  public static crrtCheckoutSuccess: any = {
    eventName: 'coins_checkout_success',
    adjustEvent: 'klk0c4',
    sendAdjustPurchase: true
  }

  /** Combined purchase revenue event for Adjust only (coins + subscriptions). */
  public static purchase: any = {
    eventName: 'purchase',
    adjustEvent: 'mib0o3',
    adjustOnly: true
  }

  public static crrtCheckoutSuccessFromMkt: any = {
    eventName: 'coins_checkout_success_from_premium',
    adjustEvent: 'nhcjtq'
  }

  public static crrtCheckoutSuccessFromSuperlike: any = {
    eventName: 'coins_checkout_success_from_superlike',
    adjustEvent: '9s3bwk'
  }

  // No Fyra Adjust token for boost checkout source yet (Firebase/Meta name only).
  public static crrtCheckoutSuccessFromBoost: any = {
    eventName: 'crrt_checkout_success_from_boost',
    adjustEvent: ''
  }

  public static checkoutStart: any = {
    eventName: 'checkout_start',
    adjustEvent: '5tb7ml'
  }

  public static checkoutAbandoned: any = {
    eventName: 'checkout_abandoned',
    adjustEvent: 'g8xtsg'
  }

  public static checkoutPayFail: any = {
    eventName: 'checkout_pay_fail',
    adjustEvent: '3j0j01'
  }

  // Unique events

  public static loginOrSignUpCompl: any = {
    eventName: 'login_or_sign_up_compl',
    adjustEvent: '7f30gv'
  }

  // Diet-specific (vegan/vegetarian/intransition): Fyra não tem segmentação por
  // dieta na UI, então estes events normalmente não disparam. Constants mantidos
  // pra paridade com Veggly. Sem token Adjust (Adjust skip), Firebase/FB seguem.
  public static loginOrSignUpComplVegan: any = {
    eventName: 'login_or_sign_up_compl_vegan',
    adjustEvent: ''
  }

  public static loginOrSignUpComplVegetarian: any = {
    eventName: 'login_or_sign_up_compl_vegetarian',
    adjustEvent: ''
  }

  public static loginOrSignUpComplInTransition: any = {
    eventName: 'login_or_sign_up_compl_intransition',
    adjustEvent: ''
  }

  public static loginOrSignUpComplOk: any = {
    eventName: 'login_or_sign_up_compl_ok',
    adjustEvent: 'tvnmpn'
  }

  public static loginOrSignUpComplOkVegan: any = {
    eventName: 'login_or_sign_up_compl_ok_vegan',
    adjustEvent: ''
  }

  public static loginOrSignUpComplOkVegetarian: any = {
    eventName: 'login_or_sign_up_compl_ok_vegetarian',
    adjustEvent: ''
  }

  public static loginOrSignUpComplOkInTransition: any = {
    eventName: 'login_or_sign_up_compl_ok_intransition',
    adjustEvent: ''
  }

  public static loginOrSignUpValuable: any = {
    eventName: 'login_or_sign_up_valuable',
    adjustEvent: 'k9flg5'
  }

  public static loginOrSignUpValuable1d: any = {
    eventName: 'login_or_sign_up_valuable_1d',
    adjustEvent: ''
  }

  public static loginOrSignUpValuableVegan: any = {
    eventName: 'login_or_sign_up_valuable_vegan',
    adjustEvent: ''
  }

  public static loginOrSignUpValuableVegetarian: any = {
    eventName: 'login_or_sign_up_valuable_vegetarian',
    adjustEvent: ''
  }

  public static loginOrSignUpValuableInTransition: any = {
    eventName: 'login_or_sign_up_valuable_intransition',
    adjustEvent: ''
  }

  public static loginOrSignUpValuableWoman: any = {
    eventName: 'login_or_sign_up_valuable_woman',
    adjustEvent: 'kgzr8p'
  }

  public static signUpComplete: any = {
    eventName: 'sign_up_complete',
    adjustEvent: '6jmv6e'
  }

  public static signUpComplOk: any = {
    eventName: 'sign_up_compl_ok',
    adjustEvent: 'ywcmtm'
  }

  public static signUpComplOkWoman: any = {
    eventName: 'sign_up_compl_ok_woman',
    adjustEvent: 'mfd7rq'
  }

  public static excludedFail: any = {
    eventName: 'excluded_fail',
    adjustEvent: '5hjluj'
  }

  public static valuableUser: any = {
    eventName: 'valuable_user',
    adjustEvent: 'v2brqp'
  }

  public static valuableUser1d: any = {
    eventName: 'valuable_user_1d',
    adjustEvent: ''
  }


  /** Isolated for unit tests — Capacitor Preferences spies are unreliable on the plugin proxy. */
  private async readStoredUserData(): Promise<any> {
    const val = await Preferences.get({ key: 'USER_DATA' });
    return val.value ? JSON.parse(val.value) : null;
  }

  registerEvent(eventDetails: any, isUnique?: boolean) {
    // Snapshot before USER_DATA read so a logout clear during the async read
    // invalidates this identity write (stale USER_DATA oid cannot re-apply).
    const readGeneration = this.firebaseAnalyticsService.getIdentityGeneration();
    void this.readStoredUserData().then((userData) => {
      this.userData = userData;

      // Best-effort identity sync for logged-in sessions only. Missing USER_DATA
      // must not enqueue setUserId(null) — that races a concurrent login and can
      // wipe a freshly applied oid. Logout uses authoritative clear instead.
      // Never await native round-trips on the event hot path.
      const userId = this.userData?._id?.$oid || null;
      if (userId) {
        void this.firebaseAnalyticsService.setUserId(userId, readGeneration).catch((error) => {
          console.error('[Firebase Analytics] Failed to set user ID:', error);
        });
      }

      if (this.userData && this.userData.excluded_fail && eventDetails?.eventName != 'excluded_fail')
        return;
      if (isUnique) {
        const diet = (this.userData &&
          this.userData.diet !== null &&
          this.userData.diet !== undefined) ? this.userData.diet : '';
        this.registerUniqueEvent(eventDetails, diet);
      } else {
        this.sendEventToAnalytics(eventDetails, false);
        if (eventDetails?.eventName.includes('from'))
          this.lastStoreFromTypeEvent = eventDetails?.eventName;
        if (eventDetails?.eventName.includes('checkout'))
          this.registerRelatedCheckoutFromEvent(eventDetails?.eventName, eventDetails?.params);
      }
    });
  }

  registerUniqueEvent(eventDetails: any, diet: string) {

    console.log('registerUniqueEvent');
    console.log(JSON.stringify(eventDetails));
    console.log(diet);
    if (eventDetails?.eventName) {
      Preferences.get({ key: eventDetails?.eventName }).then((alreadyRegistered: any) => {
        alreadyRegistered = alreadyRegistered.value ? JSON.parse(alreadyRegistered.value) : null;

        if (alreadyRegistered) {
          return;
        }

        this.sendEventToAnalytics(eventDetails, true);
        if (diet !== '' && eventDetails?.eventName.includes('login_or_sign_up')) {
          this.registerRelatedDietEvent(eventDetails?.eventName, diet)
        }
      })
    }



  }

  registerRelatedCheckoutFromEvent(mainEvent: string, params?: Record<string, string | number>) {
    /* lastStoreFromTypeEvent will be empty when purchase events arrive
    * at app startup. In this case we won't know from which screen user 
    * came from, so we don't send the related events.
    */
    if (this.lastStoreFromTypeEvent == '') {
      console.log('lastStoreFromTypeEvent is empty');
      return;
    }
    const checkoutSuccessFromEvents: any = {
      'coins_from_premium': AnalyticsService.crrtCheckoutSuccessFromMkt,
      'coins_from_superlike': AnalyticsService.crrtCheckoutSuccessFromSuperlike,
      'crrt_from_boost': AnalyticsService.crrtCheckoutSuccessFromBoost,
      'premium_from_menu': AnalyticsService.avcdCheckoutSuccessFromMkt,
      'premium_from_likedme': AnalyticsService.avcdCheckoutSuccessFromLikedMe,
      'premium_from_teleport': AnalyticsService.avcdCheckoutSuccessFromTeleport,
      'avcd_from_indicator': AnalyticsService.avcdCheckoutSuccessFromIndicator,
      'avcd_from_indicator_expl': AnalyticsService.avcdCheckoutSuccessFromIndicatorExpl,
      'premium_from_first_open': AnalyticsService.avcdCheckoutSuccessFromFirstOpen,
      'premium_from_first_photo': AnalyticsService.avcdCheckoutSuccessFromFirstPhoto,
      'premium_from_read_conf': AnalyticsService.avcdCheckoutSuccessFromReadConf,
      'premium_from_coins': AnalyticsService.avcdCheckoutSuccessFromCoins
      // premium_from_promotion has no matching *_checkout_success_* event in Adjust
    }

    const checkoutState = mainEvent.split("_").slice(-2).join("_");
    switch (checkoutState) {
      case 'checkout_success': {
        const relatedEvent = checkoutSuccessFromEvents[this.lastStoreFromTypeEvent];
        if (!relatedEvent) {
          break;
        }
        this.sendEventToAnalytics({
          ...relatedEvent,
          params: params && typeof params === 'object' ? params : {}
        }, false);
        break;
      }
    }
  }

  registerRelatedDietEvent(mainEvent: string, diet: string) {
    const complDietEvents: any = {
      'vegan': AnalyticsService.loginOrSignUpComplVegan,
      'vegetarian': AnalyticsService.loginOrSignUpComplVegetarian,
      'working': AnalyticsService.loginOrSignUpComplInTransition
    };
    const complOkDietEvents: any = {
      'vegan': AnalyticsService.loginOrSignUpComplOkVegan,
      'vegetarian': AnalyticsService.loginOrSignUpComplOkVegetarian,
      'working': AnalyticsService.loginOrSignUpComplOkInTransition
    };
    const valuableDietEvents: any = {
      'vegan': AnalyticsService.loginOrSignUpValuableVegan,
      'vegetarian': AnalyticsService.loginOrSignUpValuableVegetarian,
      'working': AnalyticsService.loginOrSignUpValuableInTransition
    };

    switch (mainEvent) {
      case 'login_or_sign_up_compl':
        this.registerUniqueEvent(complDietEvents[diet], '');
        break;
      case 'login_or_sign_up_compl_ok':
        this.registerUniqueEvent(complOkDietEvents[diet], '');
        break;
      case 'login_or_sign_up_valuable':
        this.registerUniqueEvent(valuableDietEvents[diet], '');
    }
  }

  sendEventToAnalytics(eventDetails: any, isUnique?: boolean) {
    try {
      const eventParams = eventDetails?.params && typeof eventDetails.params === 'object'
        ? eventDetails.params
        : {};
      const adjustOnly = !!eventDetails?.adjustOnly;

      // Always send to PostHog (all environments for better debugging)
      if (!adjustOnly) {
        try {
          void this.posthog.capture(eventDetails?.eventName, eventParams);
        } catch (error) {
          console.error('[PostHog] Event tracking failed:', error);
        }
      }

      if (Capacitor.isNativePlatform() && environment.production) {
        this.initializeAdjust();
        this.trackAdjustEvent(eventDetails, eventParams);

        // Combined Adjust-only purchase (coins + subs) alongside primary checkout success.
        if (eventDetails?.sendAdjustPurchase) {
          this.trackAdjustEvent(AnalyticsService.purchase, eventParams);
        }
      }

      if (!adjustOnly) {
        // Gate Firebase/GA the same way as Adjust/Facebook. Test and production share
        // the same Firebase project, so ungated events pollute production analytics.
        // Also prevents relying only on setCollectionEnabled timing for auto IAP events.
        if (environment.production) {
          this.firebaseAnalyticsService.setAnalyticsEvent(eventDetails?.eventName, eventParams);
        } else {
          console.log(`Firebase event skipped (production: false): ${eventDetails?.eventName}`);
        }

        if (Capacitor.isNativePlatform() && environment.production) {
          FacebookEvents.logEvent({
            event: eventDetails?.eventName,
            params: eventParams
          });
        }
      }

      console.log('"' + eventDetails?.eventName + '" event sent on provider.');
      if (isUnique) {
        Preferences.set({ key: eventDetails?.eventName, value: 'true' }).then(() => {
          console.log(eventDetails?.eventName + ' event saved to DB.');
        })
      }
    } catch (error) {
      // ignore reporting errors   
      console.log('Error registering a ' + eventDetails?.eventName);
      console.error(error);
    }
  }

  private trackAdjustEvent(eventDetails: any, eventParams: Record<string, string | number> = {}) {
    if (!this.adjustInitialized || !eventDetails?.adjustEvent) {
      console.warn('Adjust not initialized or no adjustEvent provided:', eventDetails);
      return;
    }

    const adjustEvent = new AdjustEvent(eventDetails.adjustEvent);
    const value = Number(eventParams?.['value']);
    const currency = eventParams?.['currency'] != null ? String(eventParams['currency']) : '';

    if (Number.isFinite(value) && currency) {
      adjustEvent.setRevenue(value, currency);
    }

    if (eventParams?.['product_id'] != null) {
      const productId = String(eventParams['product_id']);
      adjustEvent.addCallbackParameter('product_id', productId);
      adjustEvent.addPartnerParameter('product_id', productId);
    }

    Adjust.trackEvent(adjustEvent);
    console.log(
      `Adjust event sent: ${eventDetails.adjustEvent}` +
      (Number.isFinite(value) && currency ? ` (revenue: ${value} ${currency})` : '')
    );
  }

  public setAnalyticsEvent(eventName: string, eventProperties: any) {
    if (!environment.production) {
      console.log(`Firebase setAnalyticsEvent skipped (production: false): ${eventName}`);
      return;
    }
    if (Capacitor.isNativePlatform()) {
      FirebaseAnalytics.logEvent({
        name: eventName,
        params: eventProperties
      });
    } else {
      // Web platform - use native Firebase Analytics
      if (this.analytics) {
        logEvent(this.analytics, eventName, eventProperties);
      }
    }
  }

  /**
   * Sends store purchase/subscription events to Meta/TikTok with value and currency.
   * Firebase in_app_purchase is left to SDK auto-log (manual duplicates mess up GA).
   *
   * Meta server flag `app_events_if_auto_log_subs` differs per Facebook app:
   * - Veggly: OFF → auto-log sends subscriptions as Purchase (desired for campaigns)
   * - Fyra: ON  → auto-log sends subscriptions as Subscribe (Purchase only sees coins)
   *
   * Fyra strategy:
   * - TikTok: manual Purchase with value/currency (auto payment tracking disabled)
   * - Subscriptions: manually send Meta Purchase so campaigns match Veggly
   * - Do NOT manually send Meta Subscribe (auto already does)
   * - Meta coins on iOS: leave to auto-log (already includes value)
   *
   * Manual Meta Purchase must keep `_valueToSum` numeric. The iOS Facebook plugin
   * extracts it and calls `logPurchase`; string-only params used to drop the value.
   */
  public sendStorePurchaseAnalytics(
    productId: string,
    priceMicros: number,
    currency: string,
    isSubscription: boolean,
    testEventCode?: string
  ) {
    const value = priceMicros / 1000000;

    // Only send purchases to PostHog in production
    if (environment.production) {
      try {
        if (isSubscription) {
          // Extract billing cycle from product ID (e.g., 'leftypremium3months' -> '3_months')
          const billingCycle = productId.includes('3months') ? '3_months' :
                              productId.includes('6months') ? '6_months' :
                              productId.includes('12months') ? '12_months' : 'monthly';
          
          void this.posthog.trackSubscriptionPurchase(productId, value, currency, billingCycle, {
            platform: Capacitor.getPlatform(),
            test_event: testEventCode ? true : false,
          });
        } else {
          void this.posthog.trackPurchase(productId, value, currency, {
            platform: Capacitor.getPlatform(),
            test_event: testEventCode ? true : false,
            product_type: 'coins',
          });
        }
      } catch (error) {
        console.error('[PostHog] Purchase tracking failed:', error);
      }
    } else {
      console.log('[PostHog] Purchase tracking skipped (production: false)');
    }

    if (!environment.production || !Capacitor.isNativePlatform()) {
      console.log(
        `Store purchase analytics skipped for Meta/TikTok (production: ${environment.production}, native: ${Capacitor.isNativePlatform()})`
      );
      return;
    }

    const platform = Capacitor.getPlatform();

    try {
      this.sendTikTokPurchaseEvent(productId, value, currency);

      if (isSubscription) {
        // Fyra Meta auto-log uses Subscribe; also emit Purchase (with value) for campaigns
        this.sendMetaPurchaseEvent(productId, value, currency, testEventCode);
      } else if (platform === 'android') {
        this.sendMetaPurchaseEvent(productId, value, currency, testEventCode);
      } else {
        console.log(`iOS consumable Meta left to SDK auto-log: ${productId}`);
      }
    } catch (error) {
      console.error('Error sending store purchase analytics:', error);
    }
  }

  /** @deprecated Prefer sendStorePurchaseAnalytics */
  public sendFacebookPurchaseEvent(
    productId: string,
    priceMicros: number,
    currency: string,
    testEventCode?: string
  ) {
    this.sendStorePurchaseAnalytics(productId, priceMicros, currency, false, testEventCode);
  }

  private sendMetaPurchaseEvent(
    productId: string,
    value: number,
    currency: string,
    testEventCode?: string
  ) {
    const params: any = {
      _valueToSum: value,
      fb_currency: currency,
      fb_content_id: productId
    };
    if (testEventCode) {
      params._testEventCode = testEventCode;
    }

    console.log(`Sending Meta Purchase - Product: ${productId}, Value: ${value}, Currency: ${currency}`);
    FacebookEvents.logEvent({
      event: 'fb_mobile_purchase',
      params
    });
  }

  /**
   * Método para testar se o Adjust está funcionando corretamente
   * Chame este método para verificar se os eventos estão sendo enviados
   */
  public testAdjustEvent() {
    console.log('🧪 Testando evento do Adjust...');

    const testEvent = {
      eventName: 'test_adjust_event',
      adjustEvent: 'test123' // Token de teste do Adjust
    };

    this.sendEventToAnalytics(testEvent);

    console.log('✅ Teste do Adjust concluído. Verifique os logs do Adjust SDK.');
  }

}
